
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AvatarUpload } from "./form-components/AvatarUpload";
import { PersonalInfoForm } from "./form-components/PersonalInfoForm";
import { GoalsNotesForm } from "./form-components/GoalsNotesForm";
import { FormActions } from "./form-components/FormActions";
import { clientFormSchema, ClientFormValues, calculateAge } from "./form-utils/clientFormUtils";

interface AddClientFormProps {
  onSuccess?: () => void;
}

export function AddClientForm({ onSuccess }: AddClientFormProps) {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      countryCode: "+61",
      dateOfBirth: undefined,
      goals: "",
      notes: "",
    },
    mode: "onBlur" // Validate fields when they lose focus
  });

  const handlePhoneChange = (formatted: string) => {
    form.setValue("phone", formatted);
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      form.setValue("dateOfBirth", date);
      form.trigger("dateOfBirth"); // Trigger validation after setting the date
    }
  };

  const onSubmit = async (data: ClientFormValues) => {
    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to add a client",
          variant: "destructive",
        });
        return;
      }

      const { data: existingCoach, error: coachError } = await supabase
        .from("coach")
        .select("*")
        .eq("coach_id", user.id)
        .single();

      if (coachError && coachError.code !== 'PGRST116') {
        const { data: userData } = await supabase.auth.getUser();
        const userName = userData.user?.user_metadata?.full_name || 'Coach';
        
        const { error: createCoachError } = await supabase
          .from("coach")
          .insert({
            coach_id: user.id,
            full_name: userName,
          });

        if (createCoachError) {
          console.error("Error creating coach record:", createCoachError);
          toast({
            title: "Error",
            description: "There was a problem setting up your coach profile. Please try again.",
            variant: "destructive",
          });
          return;
        }
      }

      const age = calculateAge(data.dateOfBirth);
      const fullPhoneNumber = data.phone ? `${data.countryCode} ${data.phone}` : null;

      const { data: newClient, error } = await supabase
        .from("clients")
        .insert({
          full_name: data.fullName,
          coach_id: user.id,
          age: age,
          gender: data.gender || null,
          email: data.email,
          phone: fullPhoneNumber,
          goals: data.goals,
          notes: data.notes
        })
        .select()
        .single();

      if (error) {
        console.error("Error adding client:", error);
        toast({
          title: "Error",
          description: "There was a problem adding the client. Please try again.",
          variant: "destructive",
        });
        return;
      }

      await supabase.from("activities").insert({
        user_id: user.id,
        type: "new_client",
        title: "New Client Added",
        description: `A new client has joined: ${data.fullName}`,
        data: {
          client_id: newClient.client_id,
          client_name: data.fullName,
          client_email: data.email,
          client_phone: fullPhoneNumber,
          client_goals: data.goals,
          client_notes: data.notes
        }
      });

      toast({
        title: "Success",
        description: "Client added successfully",
      });

      if (onSuccess) {
        form.reset();
        setAvatarUrl(null);
        onSuccess();
      } else {
        navigate("/clients");
      }
    } catch (error) {
      console.error("Error in form submission:", error);
      toast({
        title: "Error",
        description: "There was a problem adding the client. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full mx-auto p-8 bg-gradient-to-b from-indigo-900 to-purple-900 rounded-xl shadow-xl">
      <div className="rounded-xl overflow-hidden">
        <ScrollArea className="h-[75vh]">
          <div className="px-6 py-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <AvatarUpload 
                  fullName={form.watch("fullName")} 
                  avatarUrl={avatarUrl} 
                  onAvatarChange={setAvatarUrl} 
                />
                
                <PersonalInfoForm 
                  control={form.control} 
                  onPhoneChange={handlePhoneChange}
                  onDateChange={handleDateChange}
                />
                
                <GoalsNotesForm control={form.control} />
                
                <FormActions 
                  isSubmitting={isSubmitting} 
                  onCancel={onSuccess || (() => navigate("/clients"))} 
                />
              </form>
            </Form>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
